// src/services/signageService.js
// Digital Signage Campaign Service — Backend API version
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';

const SIGNAGE_API_URL = `${NODE_BACKEND_API_URL}/signage`;

// ── Media URL helper ──
// Backend returns relative paths like "/apitest/signage/media/15/file"
// This resolves them to full URLs. Passes through blob:/data:/http URLs unchanged.
const _baseOrigin = (() => {
    try { return new URL(NODE_BACKEND_API_URL).origin; }
    catch { return ''; }
})();

export const getMediaUrl = (relativePath) => {
    if (!relativePath) return null;
    if (relativePath.startsWith('blob:') || relativePath.startsWith('data:') || relativePath.startsWith('http')) {
        return relativePath;
    }
    return `${_baseOrigin}${relativePath}`;
};

// ── Authenticated media fetch (for <img>/<video> src that require JWT) ──
// Browser <img src> / <video src> don't send Authorization headers.
// This fetches via axios (which has the auth interceptor) and returns a blob URL.
// NOTE: The cache is intentionally module-level so multiple components sharing
// the same media path don't trigger redundant downloads within a session.
// Call releaseMediaBlobCache() to free all blob URLs when a media session ends.
const _mediaBlobCache = new Map();

export const fetchMediaAsBlob = async (relativePath) => {
    if (!relativePath) return null;
    if (relativePath.startsWith('blob:') || relativePath.startsWith('data:')) return relativePath;
    if (_mediaBlobCache.has(relativePath)) return _mediaBlobCache.get(relativePath);

    try {
        const fullUrl = getMediaUrl(relativePath);
        const response = await axios.get(fullUrl, { responseType: 'blob' });
        const blobUrl = URL.createObjectURL(response.data);
        _mediaBlobCache.set(relativePath, blobUrl);
        return blobUrl;
    } catch {
        return getMediaUrl(relativePath);
    }
};

// Revoke all cached blob URLs and clear the cache.
// Call this when a media session ends (e.g. CampaignPlayer unmounts) so the
// browser can reclaim the memory held by those object URLs.
export const releaseMediaBlobCache = () => {
    _mediaBlobCache.forEach((url) => {
        if (url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    });
    _mediaBlobCache.clear();
};

// ── Campaigns CRUD ──

export const getAllCampaigns = async () => {
    const response = await axios.get(`${SIGNAGE_API_URL}/campaigns`);
    return response.data;
};

export const getCampaignById = async (campaignId) => {
    try {
        const response = await axios.get(`${SIGNAGE_API_URL}/campaigns/${campaignId}`);
        return response.data;
    } catch (error) {
        if (error.response?.status === 404) return null;
        throw error;
    }
};

export const createCampaign = async (campaignData) => {
    const response = await axios.post(`${SIGNAGE_API_URL}/campaigns`, campaignData);
    return response.data;
};

export const updateCampaign = async (campaignId, updates) => {
    const response = await axios.put(`${SIGNAGE_API_URL}/campaigns/${campaignId}`, updates);
    return response.data;
};

export const deleteCampaign = async (campaignId) => {
    await axios.delete(`${SIGNAGE_API_URL}/campaigns/${campaignId}`);
};

// ── Activation ──

export const setActiveCampaign = async (campaignId) => {
    const response = await axios.post(`${SIGNAGE_API_URL}/campaigns/${campaignId}/activate`);
    return response.data;
};

export const clearActiveCampaign = async () => {
    const response = await axios.post(`${SIGNAGE_API_URL}/campaigns/deactivate`);
    return response.data;
};

export const getActiveCampaignId = async () => {
    const response = await axios.get(`${SIGNAGE_API_URL}/active`);
    return response.data.campaign?.id || null;
};

export const getActiveCampaign = async () => {
    const response = await axios.get(`${SIGNAGE_API_URL}/active`);
    return response.data.campaign || null;
};

// Backend handles schedule filtering in GET /signage/active
export const getCurrentBookedCampaign = async () => {
    return getActiveCampaign();
};

// ── Health (admin dashboard card) ──
// One round-trip carrying organization metadata + active-campaign summary +
// per-device status badges. Consumed by <SignageHealthCard /> on signagepage.
export const getSignageHealth = async () => {
    const response = await axios.get(`${SIGNAGE_API_URL}/health`);
    return response.data;
};

// ── Slides / Media ──

export const uploadSlide = async (campaignId, file, duration = 5, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('duration', duration);

    const config = { headers: { 'Content-Type': 'multipart/form-data' } };
    if (onProgress) {
        config.onUploadProgress = (e) => {
            onProgress(Math.round((e.loaded * 100) / e.total));
        };
    }

    const response = await axios.post(
        `${SIGNAGE_API_URL}/campaigns/${campaignId}/slides`,
        formData,
        config
    );
    return response.data;
};

export const updateSlide = async (slideId, updates) => {
    const response = await axios.put(`${SIGNAGE_API_URL}/slides/${slideId}`, updates);
    return response.data;
};

export const deleteSlide = async (slideId) => {
    await axios.delete(`${SIGNAGE_API_URL}/slides/${slideId}`);
};

// ── Schedule helper (client-side for real-time UI checks) ──

export const isCampaignScheduledNow = (campaign) => {
    if (!campaign?.schedule) return false;
    const currentDay = new Date().getDay();
    const { days } = campaign.schedule;
    if (!days || days.length === 0) return true;
    const dayMap = {
        'lunes': 1, 'martes': 2, 'miercoles': 3, 'jueves': 4,
        'viernes': 5, 'sabado': 6, 'domingo': 0,
    };
    return days.some(day => dayMap[day] === currentDay);
};
